/**
 * StreamRift Sniper — the execution control pad (admin-only + password-gated).
 *
 * The /sniper route only renders for role==='admin' (App.tsx); this view then
 * requires the operator password (SniperGate). It's a control pad on the dash:
 * set the spread the Sniper grabs at, attach a match, and arm it. The relay
 * enacts every change on its next tick — this UI only writes desired state.
 */

import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import { useGate } from '@/hooks/ui/useGate';
import { SniperGate, SNIPER_GATE, SNIPER_HASH } from './SniperGate';

interface SniperConfig {
  enabled: boolean;
  clipContracts: number;
  targetLockCents: number;
  minSpreadCents: number;
  maxSpreadCents: number;
  depthSizing: boolean;
  maxDiscountCents: number;
  depthWithinCents: number;
  maxPoolContracts: number;
  [k: string]: number | boolean;
}
interface Sizing {
  clipContracts: number;
  effectiveDiscountCents: number;
  poolContracts: number;
}
interface SniperMatch {
  id: string;
  eventTicker: string;
  tickerA: string;
  tickerB: string;
  labelA: string | null;
  labelB: string | null;
  status: string;
  config: SniperConfig;
  pairsCompleted: number;
  lockedCentsTotal: number;
  live: { state?: string } | null;
  sizing: Sizing | null;
}
interface SniperHealth {
  running: boolean;
  armed: boolean;
  orgs: number;
  reason: string | null;
  runners: number;
  feed: { connected: boolean };
}
interface MatchesResp {
  matches: SniperMatch[];
  health: SniperHealth;
}

const card: CSSProperties = {
  border: '1px solid var(--border, #33384a)',
  borderRadius: 8,
  padding: '0.9rem 1.1rem',
  marginBottom: '1rem',
  background: 'var(--surface, rgba(255,255,255,0.02))',
};
const label: CSSProperties = { fontSize: '0.72rem', opacity: 0.65, display: 'block', marginBottom: 2 };
const input: CSSProperties = {
  width: 90,
  padding: '0.3rem 0.4rem',
  borderRadius: 5,
  border: '1px solid var(--border, #33384a)',
  background: 'transparent',
  color: 'inherit',
};
const btn = (accent = false): CSSProperties => ({
  padding: '0.35rem 0.7rem',
  borderRadius: 5,
  border: '1px solid var(--border, #33384a)',
  background: accent ? 'var(--accent, #2b6cb0)' : 'transparent',
  color: accent ? '#fff' : 'inherit',
  cursor: 'pointer',
  fontSize: '0.8rem',
});

function ControlPad() {
  const [data, setData] = useState<MatchesResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Grab settings — the config applied when attaching a match.
  const [spread, setSpread] = useState(4); // targetLockCents: grab pairs locking >= N¢
  const [clip, setClip] = useState(5);
  const [minSpread, setMinSpread] = useState(3);
  const [maxSpread, setMaxSpread] = useState(5);
  // Depth-walking sizer.
  const [depthSizing, setDepthSizing] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState(2);
  const [depthWithin, setDepthWithin] = useState(2);
  const [maxPool, setMaxPool] = useState(1000);
  // Attach form.
  const [eventTicker, setEventTicker] = useState('');
  const [tickerA, setTickerA] = useState('');
  const [tickerB, setTickerB] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/sniper/matches');
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    const poll = () => void refresh();
    const initial = setTimeout(poll, 0);
    const t = setInterval(poll, 4000);
    return () => {
      clearTimeout(initial);
      clearInterval(t);
    };
  }, [refresh]);

  const call = async (path: string, init: RequestInit) => {
    setBusy(true);
    try {
      const res = await apiFetch(path, { headers: { 'content-type': 'application/json' }, ...init });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const grabConfig = () => ({
    targetLockCents: spread,
    clipContracts: clip,
    minSpreadCents: minSpread,
    maxSpreadCents: maxSpread,
    depthSizing,
    maxDiscountCents: maxDiscount,
    depthWithinCents: depthWithin,
    maxPoolContracts: maxPool,
  });

  const attach = (armed: boolean) =>
    call('/api/sniper/matches', {
      method: 'POST',
      body: JSON.stringify({
        eventTicker: eventTicker.trim().toUpperCase(),
        tickerA: tickerA.trim().toUpperCase(),
        tickerB: tickerB.trim().toUpperCase(),
        config: { ...grabConfig(), enabled: armed },
      }),
    }).then(() => {
      setEventTicker('');
      setTickerA('');
      setTickerB('');
    });

  const setArm = (m: SniperMatch, enabled: boolean) =>
    call(`/api/sniper/matches/${m.id}`, { method: 'PATCH', body: JSON.stringify({ config: { enabled } }) });
  const retune = (m: SniperMatch) =>
    call(`/api/sniper/matches/${m.id}`, { method: 'PATCH', body: JSON.stringify({ config: grabConfig() }) });
  const remove = (m: SniperMatch) => call(`/api/sniper/matches/${m.id}`, { method: 'DELETE' });

  const h = data?.health;
  const dormant = !h?.running;

  return (
    <div style={{ padding: '1.25rem', maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <h1 style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>StreamRift Sniper</h1>
        <span
          style={{
            fontSize: '0.72rem',
            padding: '0.15rem 0.5rem',
            borderRadius: 999,
            background: dormant ? 'rgba(200,120,0,0.18)' : 'rgba(40,160,90,0.18)',
            color: dormant ? '#e0a030' : '#3fbf7f',
          }}
        >
          {dormant ? 'dormant' : `armed ${h?.runners ?? 0} · feed ${h?.feed.connected ? 'up' : 'down'}`}
        </span>
        <span style={{ fontSize: '0.72rem', opacity: 0.6 }}>uses your org's keys{h?.orgs ? ` · ${h.orgs} org(s) active` : ''}</span>
      </div>
      {dormant && (
        <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: 0 }}>
          Service is dormant ({h?.reason ?? 'SNIPER unset'}). Set <code>SNIPER=1</code> + a Sniper org/bot on node1 to
          run it. You can still stage matches below — they arm the moment the service wakes.
        </p>
      )}
      {error && (
        <div style={{ ...card, borderColor: 'crimson', color: 'crimson', marginBottom: '0.75rem' }}>{error}</div>
      )}

      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: '0.6rem' }}>Grab settings</div>
        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
          <div>
            <label style={label}>Spread — lock ≥ (¢)</label>
            <input style={input} type="number" min={1} max={30} value={spread} onChange={(e) => setSpread(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Clip (contracts)</label>
            <input style={input} type="number" min={1} max={500} value={clip} onChange={(e) => setClip(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Min spread (¢)</label>
            <input style={input} type="number" min={0} max={50} value={minSpread} onChange={(e) => setMinSpread(+e.target.value)} />
          </div>
          <div>
            <label style={label}>Max spread (¢)</label>
            <input style={input} type="number" min={0} max={99} value={maxSpread} onChange={(e) => setMaxSpread(+e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: '1px solid var(--border, #2a2f3e)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
            <input type="checkbox" checked={depthSizing} onChange={(e) => setDepthSizing(e.target.checked)} />
            <b>Depth-walking sizer</b> — size the clip to the pool reachable within a discount budget
          </label>
          {depthSizing && (
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <div>
                <label style={label}>Pay up to (¢ discount)</label>
                <input style={input} type="number" min={0} max={20} value={maxDiscount} onChange={(e) => setMaxDiscount(+e.target.value)} />
              </div>
              <div>
                <label style={label}>Walk depth within (¢)</label>
                <input style={input} type="number" min={0} max={20} value={depthWithin} onChange={(e) => setDepthWithin(+e.target.value)} />
              </div>
              <div>
                <label style={label}>Max pool (contracts)</label>
                <input style={input} type="number" min={1} max={200000} value={maxPool} onChange={(e) => setMaxPool(+e.target.value)} />
              </div>
            </div>
          )}
        </div>
        <p style={{ fontSize: '0.74rem', opacity: 0.6, margin: '0.6rem 0 0' }}>
          Sniper rests a post-only bid on both sibling legs whenever joining the touch locks ≥ the spread per pair, and
          re-arms after each completed pair. With the depth sizer on, the clip grows to the ask-side pool reachable
          within your discount budget (floored at the flat clip, capped at max pool) — you pay up to that many ¢ to buy
          the deeper fill. These apply to a match on attach; use “retune” to push them to an armed match.
        </p>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: '0.6rem' }}>Attach a match</div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={label}>Event ticker</label>
            <input style={{ ...input, width: 200 }} value={eventTicker} onChange={(e) => setEventTicker(e.target.value)} placeholder="KXATPMATCH-..." />
          </div>
          <div>
            <label style={label}>Ticker A</label>
            <input style={{ ...input, width: 220 }} value={tickerA} onChange={(e) => setTickerA(e.target.value)} />
          </div>
          <div>
            <label style={label}>Ticker B</label>
            <input style={{ ...input, width: 220 }} value={tickerB} onChange={(e) => setTickerB(e.target.value)} />
          </div>
          <button style={btn(true)} disabled={busy || !eventTicker || !tickerA || !tickerB} onClick={() => attach(true)}>
            Attach & Arm
          </button>
          <button style={btn()} disabled={busy || !eventTicker || !tickerA || !tickerB} onClick={() => attach(false)}>
            Attach disarmed
          </button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: '0.6rem' }}>Matches</div>
        {!data?.matches.length && <div style={{ opacity: 0.55, fontSize: '0.85rem' }}>No matches attached.</div>}
        {!!data?.matches.length && (
          <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', opacity: 0.6 }}>
                <th style={{ padding: '0.3rem 0.4rem' }}>Match</th>
                <th>Spread</th>
                <th>Clip</th>
                <th>Status</th>
                <th>Pairs</th>
                <th>Locked¢</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.matches.map((m) => {
                const armed = m.config.enabled;
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid var(--border, #2a2f3e)' }}>
                    <td style={{ padding: '0.4rem' }}>
                      <div>{m.eventTicker}</div>
                      <div style={{ opacity: 0.5, fontSize: '0.72rem' }}>{m.tickerA} · {m.tickerB}</div>
                    </td>
                    <td>{m.config.targetLockCents}¢</td>
                    <td>
                      {m.config.depthSizing && m.sizing ? (
                        <span title={`sized to pool ${m.sizing.poolContracts}, discount ${m.sizing.effectiveDiscountCents.toFixed(1)}¢`}>
                          {m.sizing.clipContracts}
                          <span style={{ opacity: 0.55, fontSize: '0.72rem' }}> @{m.sizing.effectiveDiscountCents.toFixed(1)}¢</span>
                        </span>
                      ) : (
                        m.config.clipContracts
                      )}
                    </td>
                    <td>
                      <span style={{ color: armed ? '#3fbf7f' : '#e0a030' }}>
                        {armed ? (m.live?.state ?? 'armed') : 'disarmed'}
                      </span>
                    </td>
                    <td>{m.pairsCompleted}</td>
                    <td>{m.lockedCentsTotal}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={btn(!armed)} disabled={busy} onClick={() => setArm(m, !armed)}>
                        {armed ? 'Disarm' : 'Arm'}
                      </button>{' '}
                      <button style={btn()} disabled={busy} onClick={() => retune(m)} title="push current grab settings">
                        Retune
                      </button>{' '}
                      <button style={btn()} disabled={busy} onClick={() => remove(m)}>
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function SniperView() {
  const { unlocked, unlock } = useGate(SNIPER_GATE, SNIPER_HASH);
  if (!unlocked) return <SniperGate onUnlock={unlock} />;
  return <ControlPad />;
}
